import type { NearbyAttraction } from '@/entities/attraction';
import type { AttractionCardResponse, AttractionSummary } from '@/shared/api/experience';
import type { Geo } from '@/shared/models/geo';
import type { ExperiencePhoto } from '@/widgets/photosPreview/types/experience';

import type { AttractionCard } from '../models/attractionCard';

const ATTRACTIONS_BREADCRUMB_PATH = '/attractions/';

const toNearbyAttraction = (item: AttractionSummary): NearbyAttraction => ({
  id: item.id ?? 0,
  name: item.name ?? '',
  slug: item.slug,
  description: item.description,
  address: item.address,
  excursions_count: item.excursions_count,
  latitude: item.latitude,
  longitude: item.longitude,
  citytag_url: item.slug ? `/sights/${item.id}-${item.slug}` : undefined,
  cover_photo: {
    thumbnail: item.photo_url,
    medium: item.photo_url,
  },
  experience_main_photo_url: item.photo_url,
});

/** Гео берём из первой экскурсии: отдельного поля с городом в ответе нет. */
const mapGeo = (response: AttractionCardResponse): Geo => {
  const experienceGeo = response.related_experiences?.results?.[0]?.geo;

  if (!experienceGeo) {
    return { city: [], country: [], region: [] };
  }

  const addUrl = (items: { id?: number; name?: string; slug?: string }[]) =>
    items.map((item) => ({
      id: item.id ?? 0,
      name: item.name ?? '',
      slug: item.slug ?? '',
      url: item.slug ? `/experience/${item.slug}/` : '',
    }));

  return {
    city: addUrl(experienceGeo.city ?? []),
    country: addUrl(experienceGeo.country ?? []),
    region: addUrl(experienceGeo.region ?? []),
  };
};

export const mapAttractionCard = (response: AttractionCardResponse): AttractionCard => {
  const attraction = response.attraction ?? {};
  const canonicalUrl = response.metatags?.canonical_url ?? '';

  const photos: ExperiencePhoto[] = (response.photos ?? []).map((photo) => ({
    medium: photo.medium,
    thumbnail: photo.thumbnail,
    type: 'photo',
  }));

  const geo = mapGeo(response);
  const city = geo.city?.[0];
  const country = geo.country?.[0];

  const hasCoordinates = attraction.latitude != null && attraction.longitude != null;

  return {
    id: attraction.id ?? 0,
    slug: attraction.slug ?? '',
    // в metatags.h1 лежит SEO-заголовок листинга («Экскурсии в дом Бенуа»), карточке нужно имя места
    name: attraction.name || response.metatags?.h1 || '',
    description: attraction.description ?? '',

    // у крошки текущей страницы адреса нет, пустой `to` оставляет её без ссылки
    breadcrumbs: (response.breadcrumbs ?? []).flatMap(({ name, url }) =>
      name ? [{ label: name, to: url ?? '' }] : [],
    ),

    photos,
    galleryExperience: {
      id: attraction.id ?? 0,
      title: attraction.name ?? '',
      url: canonicalUrl,
      photos,
    },

    info: {
      address: attraction.address,
    },

    location: hasCoordinates
      ? {
          description: attraction.address,
          latitude: attraction.latitude as number,
          longitude: attraction.longitude as number,
        }
      : undefined,

    schedule: (response.schedule ?? []).map((event) => ({
      id: event.exp_id ?? 0,
      title: event.exp_title ?? '',
      url: event.exp_url ?? '',
      startsAt: event.event_dt,
      priceText: event.price_text,
    })),

    experiences: {
      count: attraction.excursions_count ?? response.related_experiences?.count ?? 0,
      url: response.experiences_url ?? '',
    },

    nearby: {
      // «в Санкт-Петербурге» — подставляется в заголовок блока на странице
      cityPhrase: response.city_in_obj_phrase,
      url: response.breadcrumbs?.find(({ url }) => url?.includes(ATTRACTIONS_BREADCRUMB_PATH))?.url ?? '',
      items: (response.related_attractions ?? []).map(toNearbyAttraction),
      analytics: {
        city_id: city?.id,
        city_name: city?.name,
        country_id: country?.id,
        country_name: country?.name,
        subject_geo_id: city?.id,
        subject_geo_name: city?.name,
        subject_geo_type: 'city',
      },
    },

    faq: {
      title: response.faq?.title ?? '',
      items: (response.faq?.items ?? []).map((item) => ({
        question: item.question ?? '',
        answerHtml: item.answer ?? '',
      })),
    },
  };
};
